import { useState, useEffect, useRef } from "react";
import axios from "axios";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { uploadWithReceipt } from "@/lib/bulkUploadReceipt";
import { 
  Package, Layers, Plus, Search, ChevronRight, Lock, Unlock, 
  Edit, Trash2, Copy, FileSpreadsheet, ArrowRight, Download, Upload, RefreshCw, Database, Tag, AlertTriangle, Check, X, Loader2, FileText, Image as ImageIcon, Images
} from "lucide-react";
import { Button } from "@/components/ui/button";
import PermButton from "@/components/PermButton";
import SKUAssetsDialog from "@/components/SKUAssetsDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import useAuthStore from "../store/authStore";
import Pagination from "../components/Pagination";
import BuyerSkuSearchBox from "../components/BuyerSkuSearchBox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Unique testid per brand-BOM row: first occurrence keeps `<rm_id>`, later
// duplicates of the same rm_id get `<rm_id>-dup1`, `-dup2`, ...
const brandRowTestId = (rows, idx) => {
  const rmId = rows[idx]?.rm_id;
  const n = rows.slice(0, idx).filter((r) => r.rm_id === rmId).length;
  return n === 0 ? rmId : `${rmId}-dup${n}`;
};

const SKUManagement = () => {
  // Get token from zustand auth store
  const token = useAuthStore((state) => state.token);
  // Feb 2026 hotfix: pull RBAC roles from the shared auth store instead of
  // re-fetching `/auth/me` on every Full-BOM dialog open. `/auth/me`
  // returns only the legacy singular `role` string, which for some seed
  // users (e.g. `branchops@bidso.com`) is `branch_user` — mismatched
  // against the RBAC role code `BRANCH_OPS_USER` that actually governs
  // Supply approval. The store's `userRoles` is populated by
  // `/auth/permissions` at login, which returns proper RBAC role codes
  // (`get_my_permissions` at `auth_routes.py:518` → `rbac_service.get_user_roles`).
  // Same source of truth as `authStore.isMasterAdmin`, `hasRole`,
  // `PermButton`, etc. — one fix, no per-page role-fetch divergence.
  const userRoles = useAuthStore((state) => state.userRoles);
  const authUser = useAuthStore((state) => state.user);
  const fetchPermissions = useAuthStore((state) => state.fetchPermissions);
  // Feb 2026 — `hasPermission` is required by `_canReadBomApproval`
  // (Dual-Track Approval panel visibility widened to read-only viewers).
  // MASTER_ADMIN bypass is baked into the store selector (`authStore.js:73`).
  const hasPermission = useAuthStore((state) => state.hasPermission);

  // Helper to get auth headers
  const getHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [activeTab, setActiveTab] = useState("bidso");
  // Oct 2026 — Image Assets view ({ level: "BIDSO" | "BUYER", skuId }).
  const [assetsTarget, setAssetsTarget] = useState(null);
  
  // Data
  const [bidsoSKUs, setBidsoSKUs] = useState([]);
  const [buyerSKUs, setBuyerSKUs] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [models, setModels] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [filterVertical, setFilterVertical] = useState("");
  const [filterModel, setFilterModel] = useState("");
  const [filterBrand, setFilterBrand] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Selected items
  const [selectedBidso, setSelectedBidso] = useState(null);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  
  // Dialogs
  const [showBuyerDialog, setShowBuyerDialog] = useState(false);
  const [showBOMDialog, setShowBOMDialog] = useState(false);

  // Create-Bidso-SKU dialog (Feb 2026 — Composer Path 1 step 1).
  // License dropdown is fed by the master `/api/licenses` endpoint so designers
  // can only pick a pre-registered license code (no free-text).
  const [createBidsoOpen, setCreateBidsoOpen] = useState(false);
  const [createBidsoForm, setCreateBidsoForm] = useState({ vertical_id: "", model_id: "", license_code: "", name: "" });
  const [licensesList, setLicensesList] = useState([]);
  const [creatingBidso, setCreatingBidso] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/licenses`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setLicensesList(Array.isArray(res.data) ? res.data : []))
      .catch(() => setLicensesList([]));
  }, [token]);

  const submitCreateBidso = async () => {
    if (!createBidsoForm.vertical_id || !createBidsoForm.model_id) {
      toast.error("Pick a vertical and model");
      return;
    }
    setCreatingBidso(true);
    try {
      const res = await axios.post(
        `${API}/bidso-composer/draft`,
        {
          vertical_id: createBidsoForm.vertical_id,
          model_id: createBidsoForm.model_id,
          license_code: (createBidsoForm.license_code || "").toUpperCase(),
          name: createBidsoForm.name || "",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const newSku = res.data?.bidso_sku;
      toast.success(`Bidso SKU created: ${newSku?.bidso_sku_id} (DRAFT_NO_BOM)`);
      setCreateBidsoOpen(false);
      setCreateBidsoForm({ vertical_id: "", model_id: "", license_code: "", name: "" });
      // Refresh local SKU list + tell composer landing to refresh.
      // `fetchBidsoSKUs` / `fetchBuyerSKUs` are the same refresh pair
      // used everywhere else in this file (see lines 249-251, 385-388,
      // etc.); the earlier `loadData()` reference was dead — never
      // defined in this component — and just silently no-op'd.
      try { fetchBidsoSKUs(); fetchBuyerSKUs(); } catch (e) { /* ignore refresh failures */ }
      window.dispatchEvent(new Event("bidso-composer:refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create Bidso SKU");
    } finally {
      setCreatingBidso(false);
    }
  };
  const [showBulkCreateDialog, setShowBulkCreateDialog] = useState(false);
  
  // Forms
  const [buyerForm, setBuyerForm] = useState({
    bidso_sku_id: "",
    brand_id: "",
    name: "",
    description: ""
  });
  const [suggestedCode, setSuggestedCode] = useState("");
  const [previewSKUID, setPreviewSKUID] = useState("");
  
  // BOM
  const [commonBOM, setCommonBOM] = useState([]);
  const [brandSpecificBOM, setBrandSpecificBOM] = useState([]);
  const [bomLocked, setBomLocked] = useState(false);
  
  // Buyer SKU Full BOM
  const [showBuyerBOMDialog, setShowBuyerBOMDialog] = useState(false);
  const [selectedBuyerSKU, setSelectedBuyerSKU] = useState(null);
  const [buyerFullBOM, setBuyerFullBOM] = useState(null);
  const [buyerBOMLoading, setBuyerBOMLoading] = useState(false);
  const [bomApproval, setBomApproval] = useState(null);       // approval state on the open buyer SKU
  const [approvalActionLoading, setApprovalActionLoading] = useState(false);
  
  // BOM Item Edit
  const [editingBOMItem, setEditingBOMItem] = useState(null);
  const [pendingDeleteBOMItem, setPendingDeleteBOMItem] = useState(null);
  const [showBOMEditDialog, setShowBOMEditDialog] = useState(false);

  // Tech Pack (Feb 2026) — per-row image uploads + on-demand PDF download.
  // Fixed 6-slot set (mirrors backend `ALLOWED_SLOTS` in techpack_assembly).
  const TECHPACK_SLOTS = [
    { key: "hero_render",       label: "Hero Render"       },
    { key: "sticker_placement", label: "Sticker Placement" },
    { key: "print_guide_1",     label: "Print Guide 1"     },
    { key: "print_guide_2",     label: "Print Guide 2"     },
    { key: "print_guide_3",     label: "Print Guide 3"     },
    { key: "print_guide_4",     label: "Print Guide 4"     },
  ];
  const [showTechpackDialog, setShowTechpackDialog] = useState(false);
  const [techpackSku, setTechpackSku] = useState(null);   // the buyer SKU under edit
  const [techpackSlots, setTechpackSlots] = useState([]); // current upload state per slot
  const [techpackBusySlot, setTechpackBusySlot] = useState(null);
  const [techpackLoading, setTechpackLoading] = useState(false);
  const [bomEditForm, setBomEditForm] = useState({ rm_id: "", new_rm_id: "", quantity: 1, unit: "PCS" });
  const [bomEditLoading, setBomEditLoading] = useState(false);
  const [scheduleWarning, setScheduleWarning] = useState(null);
  
  // Edit Buyer SKU (HSN/GST)
  const [showEditBuyerDialog, setShowEditBuyerDialog] = useState(false);
  const [editBuyerForm, setEditBuyerForm] = useState({ hsn_code: "", gst_rate: 18 });
  const [editBuyerLoading, setEditBuyerLoading] = useState(false);
  
  // Bulk HSN/GST Upload
  const [showBulkHSNDialog, setShowBulkHSNDialog] = useState(false);
  const [bulkHSNFile, setBulkHSNFile] = useState(null);
  const [bulkHSNLoading, setBulkHSNLoading] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    bidso_skus: 0,
    buyer_skus: 0,
    common_boms: 0
  });
  
  // Data Sync
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  
  // Bulk Delete
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeletePreview, setBulkDeletePreview] = useState(null);

  // Data Integrity
  const [showIntegrityDialog, setShowIntegrityDialog] = useState(false);
  const [integrityData, setIntegrityData] = useState(null);
  const [integrityLoading, setIntegrityLoading] = useState(false);
  const [repairLoading, setRepairLoading] = useState(false);
  const [bulkDeleteFile, setBulkDeleteFile] = useState(null);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const bulkDeleteFileRef = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);
  const syncFileInputRef = useRef(null);
  
  // Bulk BOM Upload
  const [showBOMUploadDialog, setShowBOMUploadDialog] = useState(false);
  const [bomUploadLoading, setBomUploadLoading] = useState(false);
  const [bomUploadResult, setBomUploadResult] = useState(null);
  const [bomUploadMode, setBomUploadMode] = useState("merge");
  const bomFileInputRef = useRef(null);

  // Bulk SKU Import
  const [skuImportLoading, setSkuImportLoading] = useState(false);
  const skuImportFileRef = useRef(null);

  // Bulk Bidso SKU Import (preserves existing bidso_sku_id; skips duplicates)
  const [bidsoImportLoading, setBidsoImportLoading] = useState(false);
  const [bidsoImportResult, setBidsoImportResult] = useState(null);
  const bidsoImportFileRef = useRef(null);

  // Feb 2026 — Phase 2 migration (defect A). reqId stale-guards so an
  // older list response cannot overwrite a newer one.
  const bidsoListReqIdRef = useRef(0);
  const buyerListReqIdRef = useRef(0);

  // Pagination state for Bidso SKUs
  const [bidsoPage, setBidsoPage] = useState(1);
  const [bidsoPageSize, setBidsoPageSize] = useState(50);
  const [bidsoTotal, setBidsoTotal] = useState(0);
  const [bidsoTotalPages, setBidsoTotalPages] = useState(1);

  // Pagination state for Buyer SKUs
  const [buyerPage, setBuyerPage] = useState(1);
  const [buyerPageSize, setBuyerPageSize] = useState(50);
  const [buyerTotal, setBuyerTotal] = useState(0);
  const [buyerTotalPages, setBuyerTotalPages] = useState(1);

  useEffect(() => {
    fetchMasterData();
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === "bidso") {
      fetchBidsoSKUs();
    } else {
      fetchBuyerSKUs();
    }
  }, [activeTab, filterVertical, filterModel, filterBrand, bidsoPage, bidsoPageSize, buyerPage, buyerPageSize]);

  // Clear selectedBidso when switching to buyer tab without a specific selection
  useEffect(() => {
    if (activeTab === "buyer" && !filterBrand) {
      setSelectedBidso(null);
    }
  }, [activeTab]);

  const fetchMasterData = async () => {
    try {
      const headers = getHeaders();
      const [verticalsRes, modelsRes, brandsRes] = await Promise.all([
        axios.get(`${API}/verticals`, { headers }),
        axios.get(`${API}/models`, { headers }),
        axios.get(`${API}/brands`, { headers }),
      ]);
      setVerticals(verticalsRes.data.filter(v => v.status === 'ACTIVE'));
      setModels(modelsRes.data.filter(m => m.status === 'ACTIVE'));
      setBrands(brandsRes.data.filter(b => b.status === 'ACTIVE'));
    } catch (error) {
      toast.error("Failed to fetch master data");
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/sku-management/migration-stats`, { headers: getHeaders() });
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch stats");
    }
  };

  const fetchBidsoSKUs = async () => {
    const myReqId = ++bidsoListReqIdRef.current;
    setLoading(true);
    try {
      let url = `${API}/sku-management/bidso-skus?page=${bidsoPage}&page_size=${bidsoPageSize}`;
      if (filterVertical) url += `&vertical_id=${filterVertical}`;
      if (filterModel) {
        const selectedModel = models.find(m => m.id === filterModel);
        url += `&model_code=${encodeURIComponent(selectedModel.code)}`;
      }
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await axios.get(url, { headers: getHeaders() });
      if (myReqId !== bidsoListReqIdRef.current) return;
      setBidsoSKUs(res.data.items || []);
      setBidsoTotal(res.data.total || 0);
      setBidsoTotalPages(res.data.total_pages || 1);
    } catch (error) {
      if (myReqId !== bidsoListReqIdRef.current) return;
      toast.error("Failed to fetch Bidso SKUs");
    } finally {
      if (myReqId === bidsoListReqIdRef.current) setLoading(false);
    }
  };

  const fetchBuyerSKUs = async () => {
    const myReqId = ++buyerListReqIdRef.current;
    setLoading(true);
    try {
      let url = `${API}/sku-management/buyer-skus?page=${buyerPage}&page_size=${buyerPageSize}`;
      if (filterBrand) url += `&brand_id=${filterBrand}`;
      if (selectedBidso) url += `&bidso_sku_id=${selectedBidso.bidso_sku_id}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await axios.get(url, { headers: getHeaders() });
      if (myReqId !== buyerListReqIdRef.current) return;
      const items = res.data.items || [];
      setBuyerSKUs(items);
      setBuyerTotal(res.data.total || 0);
      setBuyerTotalPages(res.data.total_pages || 1);
    } catch (error) {
      if (myReqId !== buyerListReqIdRef.current) return;
      toast.error("Failed to fetch Buyer SKUs");
    } finally {
      if (myReqId === buyerListReqIdRef.current) setLoading(false);
    }
  };

  const fetchNextCode = async (verticalId, modelId) => {
    if (!verticalId || !modelId) return;
    try {
      const res = await axios.get(`${API}/sku-management/bidso-skus/next-code?vertical_id=${verticalId}&model_id=${modelId}`, { headers: getHeaders() });
      setSuggestedCode(res.data.suggested_code);
      setPreviewSKUID(res.data.preview_sku_id);
      // `setBidsoForm` was removed when the Bidso create flow migrated
      // to `createBidsoForm`, but this write to the old setter was
      // never cleaned up. `createBidsoForm` has no `numeric_code`
      // field (see line 84 — the composer flow auto-assigns), so no
      // replacement write is needed. Kept as a comment so a future
      // reader knows this was audited, not overlooked.
    } catch (error) {
      console.error("Failed to get next code");
    }
  };

  // Pagination handlers
  const handleBidsoPageChange = (page) => {
    setBidsoPage(page);
  };

  const handleBidsoPageSizeChange = (size) => {
    setBidsoPageSize(size);
    setBidsoPage(1); // Reset to first page when changing page size
  };

  const handleBuyerPageChange = (page) => {
    setBuyerPage(page);
  };

  const handleBuyerPageSizeChange = (size) => {
    setBuyerPageSize(size);
    setBuyerPage(1);
  };

  // Reset pagination when filters change
  const handleFilterVerticalChange = (value) => {
    setFilterVertical(value);
    setBidsoPage(1);
  };

  const handleFilterModelChange = (value) => {
    setFilterModel(value);
    setBidsoPage(1);
  };

  const handleFilterBrandChange = (value) => {
    setFilterBrand(value);
    setBuyerPage(1);
  };

  const handleSearch = () => {
    if (activeTab === "bidso") {
      setBidsoPage(1);
      fetchBidsoSKUs();
    } else {
      setBuyerPage(1);
      fetchBuyerSKUs();
    }
  };

  const handleCreateBuyer = async () => {
    try {
      await axios.post(`${API}/sku-management/buyer-skus`, buyerForm, { headers: getHeaders() });
      toast.success("Buyer SKU created successfully");
      setShowBuyerDialog(false);
      setBuyerForm({ bidso_sku_id: "", brand_id: "", name: "", description: "" });
      fetchBuyerSKUs();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create Buyer SKU");
    }
  };

  const handleBulkCreateBuyerSKUs = async (brandIds) => {
    if (!selectedBidso || brandIds.length === 0) return;
    try {
      const res = await axios.post(
        `${API}/sku-management/buyer-skus/bulk-create?bidso_sku_id=${selectedBidso.bidso_sku_id}`,
        brandIds,
        { headers: getHeaders() }
      );
      toast.success(`Created ${res.data.created} Buyer SKUs, ${res.data.skipped} skipped`);
      setShowBulkCreateDialog(false);
      fetchBuyerSKUs();
      fetchStats();
    } catch (error) {
      toast.error("Failed to bulk create Buyer SKUs");
    }
  };

  const handleViewBOM = async (bidsoSKU) => {
    setSelectedBidso(bidsoSKU);
    try {
      const res = await axios.get(`${API}/sku-management/bom/common/${bidsoSKU.bidso_sku_id}`, { headers: getHeaders() });
      setCommonBOM(res.data.items || []);
      setBomLocked(res.data.is_locked || false);
      setShowBOMDialog(true);
    } catch (error) {
      toast.error("Failed to fetch BOM");
    }
  };

  const handleViewBuyerBOM = async (buyerSKU) => {
    setSelectedBuyerSKU(buyerSKU);
    setBuyerBOMLoading(true);
    setShowBuyerBOMDialog(true);
    setBomApproval(null);
    // Safety net: if the login-time /auth/permissions fetch was aborted
    // (e.g. by a fast route change), userRoles will be empty and the
    // Dual-Track Approval panel would stay hidden even for entitled users.
    // Kick a refetch — it's idempotent, cheap, and awaits, so the panel
    // renders with the correct predicate below on this dialog open.
    if (token && (!userRoles || userRoles.length === 0)) {
      try { await fetchPermissions(token); } catch (_) { /* non-fatal */ }
    }
    try {
      const [bomRes, approvalRes] = await Promise.all([
        axios.get(`${API}/sku-management/bom/full/${buyerSKU.buyer_sku_id}`, { headers: getHeaders() }),
        axios.get(`${API}/sku-management/buyer-skus/${buyerSKU.buyer_sku_id}/bom-approval`, { headers: getHeaders() })
          .catch(() => ({ data: null })),
      ]);
      setBuyerFullBOM(bomRes.data);
      setBomApproval(approvalRes.data);
    } catch (error) {
      toast.error("Failed to fetch Buyer SKU BOM");
      setBuyerFullBOM(null);
    } finally {
      setBuyerBOMLoading(false);
    }
  };

  const handleBomApprove = async (track) => {
    if (!selectedBuyerSKU) return;
    setApprovalActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/sku-management/buyer-skus/${selectedBuyerSKU.buyer_sku_id}/bom-approval/${track}`,
        {},
        { headers: getHeaders() },
      );
      setBomApproval(res.data.approval);
      toast.success(`${track === "supply" ? "Supply" : "Demand"} approval recorded`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Approval failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setApprovalActionLoading(false);
    }
  };

  const handleBomUnlock = async () => {
    if (!selectedBuyerSKU) return;
    if (!window.confirm("Unlock the BOM? This clears BOTH approval tracks — both teams will need to re-approve.")) return;
    setApprovalActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/sku-management/buyer-skus/${selectedBuyerSKU.buyer_sku_id}/bom-approval/unlock`,
        {},
        { headers: getHeaders() },
      );
      setBomApproval(res.data.approval);
      toast.success("BOM unlocked");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Unlock failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setApprovalActionLoading(false);
    }
  };

  // RBAC-role predicates. Read from the shared auth store (RBAC-source-
  // of-truth via /auth/permissions), with a legacy `user.role === "master_admin"`
  // fallback for the login-first-tick before the permissions roundtrip
  // resolves — mirrors `authStore.isMasterAdmin` exactly (`authStore.js:54-57`).
  const _isMasterAdmin = () =>
    (userRoles || []).includes("MASTER_ADMIN") || authUser?.role === "master_admin";
  const _canApproveSupply = () =>
    _isMasterAdmin() || (userRoles || []).includes("BRANCH_OPS_USER");
  const _canApproveDemand = () =>
    _isMasterAdmin() || (userRoles || []).includes("DEMAND_PLANNER");
  // Feb 2026 — Dual-Track Approval panel visibility to read-only viewers.
  // Anyone holding `BomApproval:READ` (e.g. TECH_OPS_ENGINEER) should see
  // the panel on every SKU including ones pending on both tracks — that's
  // precisely the case they most need to follow up on. The two APPROVE
  // gates above are UNCHANGED; the per-track button gates at :2109 and
  // :2152 already `else`-branch to the "Pending ... review" italic label,
  // which IS the view-only form (AC2). `hasPermission` from authStore
  // handles the MASTER_ADMIN bypass natively (authStore.js:73). Legacy
  // `master_admin` role covers the login-first-tick before the
  // permissions roundtrip resolves, matching the other predicates.
  const _canReadBomApproval = () =>
    hasPermission("BomApproval", "READ") || authUser?.role === "master_admin";

  const handleEditBOMItem = async (item) => {
    if (!selectedBuyerSKU) return;
    setEditingBOMItem(item);
    setBomEditForm({
      rm_id: item.rm_id,
      new_rm_id: item.rm_id,
      quantity: item.quantity,
      unit: item.unit || "PCS"
    });
    setScheduleWarning(null);
    
    // Check if scheduled for production in next 10 days
    try {
      const res = await axios.get(
        `${API}/sku-management/bom/buyer-sku/${selectedBuyerSKU.buyer_sku_id}/check-schedule`,
        { headers: getHeaders() }
      );
      if (res.data.is_scheduled) {
        setScheduleWarning({
          count: res.data.schedule_count,
          branches: res.data.branches
        });
      }
    } catch (error) {
      console.error("Failed to check schedule:", error);
    }
    
    setShowBOMEditDialog(true);
  };

  const handleSaveBOMEdit = async () => {
    if (!selectedBuyerSKU || !editingBOMItem) return;
    setBomEditLoading(true);
    
    try {
      const res = await axios.put(
        `${API}/sku-management/bom/buyer-sku/${selectedBuyerSKU.buyer_sku_id}/item`,
        {
          rm_id: editingBOMItem.rm_id,
          new_rm_id: bomEditForm.new_rm_id !== editingBOMItem.rm_id ? bomEditForm.new_rm_id : null,
          quantity: parseFloat(bomEditForm.quantity),
          unit: bomEditForm.unit,
          line_id: editingBOMItem.line_id ?? null,  // permanent row tag — rm_id may be duplicated
          seen_quantity: editingBOMItem.quantity ?? null,  // refused if the row changed since load
          seen_unit: editingBOMItem.unit ?? null,
        },
        { headers: getHeaders() }
      );
      
      if (res.data.status === "PENDING_APPROVAL") {
        toast.info(res.data.message);
      } else {
        toast.success("BOM item updated successfully");
        // Refresh BOM data
        const bomRes = await axios.get(`${API}/sku-management/bom/full/${selectedBuyerSKU.buyer_sku_id}`, { headers: getHeaders() });
        setBuyerFullBOM(bomRes.data);
      }
      
      setShowBOMEditDialog(false);
      setEditingBOMItem(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update BOM item");
    } finally {
      setBomEditLoading(false);
    }
  };

  const handleDeleteBOMItem = async (item) => {
    if (!selectedBuyerSKU) return;
    setPendingDeleteBOMItem(null);
    
    try {
      const res = await axios.delete(
        `${API}/sku-management/bom/buyer-sku/${selectedBuyerSKU.buyer_sku_id}/item/${item.rm_id}`,
        // Permanent row tag: removes only this row even when rm_id is duplicated.
        { headers: getHeaders(), params: item.line_id
          ? { line_id: item.line_id, seen_quantity: item.quantity, seen_unit: item.unit }
          : {} }
      );
      
      if (res.data.status === "PENDING_APPROVAL") {
        toast.info("Removal requires Master Admin approval. Request submitted.");
      } else {
        toast.success("BOM item removed successfully");
        const bomRes = await axios.get(`${API}/sku-management/bom/full/${selectedBuyerSKU.buyer_sku_id}`, { headers: getHeaders() });
        setBuyerFullBOM(bomRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove BOM item");
    }
  };

  const handleLockBOM = async () => {
    if (!selectedBidso) return;
    try {
      await axios.post(`${API}/sku-management/bom/common/${selectedBidso.bidso_sku_id}/lock`, {}, { headers: getHeaders() });
      toast.success("BOM locked successfully");
      setBomLocked(true);
    } catch (error) {
      toast.error("Failed to lock BOM");
    }
  };

  const handleUnlockBOM = async () => {
    if (!selectedBidso) return;
    try {
      await axios.post(`${API}/sku-management/bom/common/${selectedBidso.bidso_sku_id}/unlock`, {}, { headers: getHeaders() });
      toast.success("BOM unlocked");
      setBomLocked(false);
    } catch (error) {
      toast.error("Failed to unlock BOM");
    }
  };

  const getVerticalName = (id) => verticals.find(v => v.id === id)?.name || "";
  const getModelName = (id) => models.find(m => m.id === id)?.name || "";
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || "";
  const getFilteredModels = () => filterVertical ? models.filter(m => m.vertical_id === filterVertical) : models;

  // Edit Buyer SKU (HSN/GST)
  const handleEditBuyerSKU = (sku) => {
    setSelectedBuyerSKU(sku);
    setEditBuyerForm({
      hsn_code: sku.hsn_code || "",
      gst_rate: sku.gst_rate || 18
    });
    setShowEditBuyerDialog(true);
  };

  // --- Tech Pack handlers (Feb 2026) ----------------------------------
  // openTechpackDialog: load the current 6-slot upload state from the
  // server so the dialog can show which slots are filled. Always calls
  // /techpack/images (never cached) so two admins editing concurrently
  // see fresh state on reopen.
  const openTechpackDialog = async (sku) => {
    setTechpackSku(sku);
    setShowTechpackDialog(true);
    setTechpackLoading(true);
    try {
      const res = await axios.get(
        `${API}/buyer-skus/${sku.buyer_sku_id}/techpack/images`,
        { headers: getHeaders() }
      );
      setTechpackSlots(res.data?.slots || []);
    } catch (e) {
      toast.error("Failed to load Tech Pack image state");
      setTechpackSlots([]);
    } finally {
      setTechpackLoading(false);
    }
  };

  const refreshTechpackSlots = async (buyerSkuId) => {
    try {
      const res = await axios.get(
        `${API}/buyer-skus/${buyerSkuId}/techpack/images`,
        { headers: getHeaders() }
      );
      setTechpackSlots(res.data?.slots || []);
    } catch (_e) { /* swallow — toast already raised */ }
  };

  // Upload one image into one slot. We POST as multipart so FastAPI's
  // UploadFile reads the content_type the browser tagged the file with
  // (so the backend's 415 check is exercised exactly as documented).
  const uploadTechpackImage = async (buyerSkuId, slot, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`File too large (max 10 MB)`);
      return;
    }
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast.error("Only PNG and JPEG are allowed");
      return;
    }
    setTechpackBusySlot(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(
        `${API}/buyer-skus/${buyerSkuId}/techpack/images/${slot}`,
        fd,
        { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${slot} uploaded`);
      await refreshTechpackSlots(buyerSkuId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Upload failed for ${slot}`);
    } finally {
      setTechpackBusySlot(null);
    }
  };

  const deleteTechpackImage = async (buyerSkuId, slot) => {
    setTechpackBusySlot(slot);
    try {
      await axios.delete(
        `${API}/buyer-skus/${buyerSkuId}/techpack/images/${slot}`,
        { headers: getHeaders() }
      );
      toast.success(`${slot} cleared`);
      await refreshTechpackSlots(buyerSkuId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to clear ${slot}`);
    } finally {
      setTechpackBusySlot(null);
    }
  };

  // Download the PDF as a Blob, then trigger a browser download. Matches
  // the existing download-link pattern elsewhere in this file.
  // Feb 2026 — append `?ts=<Date.now()>` so any SKU already sitting in
  // the browser HTTP cache or Cloudflare's edge cache is guaranteed a
  // miss (unique URL = no key collision). Backend also returns
  // `Cache-Control: no-store, private` so future entries are never
  // cached in the first place.
  const downloadTechpack = async (sku) => {
    try {
      const res = await axios.get(
        `${API}/buyer-skus/${sku.buyer_sku_id}/techpack.pdf?ts=${Date.now()}`,
        {
          headers: { ...getHeaders(), "Cache-Control": "no-cache" },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sku.buyer_sku_id}-TechPack.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Tech Pack downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download Tech Pack");
    }
  };
  // --- end Tech Pack handlers -----------------------------------------

  const handleSaveBuyerHSNGST = async () => {
    if (!selectedBuyerSKU) return;
    
    setEditBuyerLoading(true);
    try {
      await axios.put(
        `${API}/sku-management/buyer-skus/${selectedBuyerSKU.buyer_sku_id}/hsn-gst`,
        {
          hsn_code: editBuyerForm.hsn_code,
          gst_rate: parseFloat(editBuyerForm.gst_rate) || 18
        },
        { headers: getHeaders() }
      );
      
      toast.success(`HSN/GST updated for ${selectedBuyerSKU.buyer_sku_id}`);
      setShowEditBuyerDialog(false);
      fetchBuyerSKUs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update HSN/GST");
    } finally {
      setEditBuyerLoading(false);
    }
  };

  // Bulk HSN/GST Upload
  const handleDownloadHSNTemplate = () => {
    const template = [
      { buyer_sku_id: "EXAMPLE_SKU_001", hsn_code: "87141090", gst_rate: 18 },
      { buyer_sku_id: "EXAMPLE_SKU_002", hsn_code: "87149990", gst_rate: 18 }
    ];
    
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HSN_GST_Template");
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(data, 'hsn_gst_template.xlsx');
    toast.success("Template downloaded");
  };

  const handleBulkHSNUpload = async () => {
    if (!bulkHSNFile) {
      toast.error("Please select a file");
      return;
    }
    
    setBulkHSNLoading(true);
    const formData = new FormData();
    formData.append("file", bulkHSNFile);
    
    try {
      const res = await axios.post(`${API}/sku-management/buyer-skus/bulk-hsn-gst`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" }
      });
      
      toast.success(`Updated ${res.data.updated} SKUs, ${res.data.errors?.length || 0} errors`);
      setShowBulkHSNDialog(false);
      setBulkHSNFile(null);
      fetchBuyerSKUs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload HSN/GST data");
    } finally {
      setBulkHSNLoading(false);
    }
  };

  // Download Full Filtered SKU Data
  const handleDownloadSKUs = async (type) => {
    setExportLoading(true);
    try {
      if (type === "bidso") {
        // Fetch ALL filtered Bidso SKUs
        let url = `${API}/sku-management/bidso-skus?page=1&page_size=10000`;
        if (filterVertical) url += `&vertical_id=${filterVertical}`;
        if (filterModel) {
          const selectedModel = models.find(m => m.id === filterModel);
          url += `&model_code=${encodeURIComponent(selectedModel.code)}`;
        }
        if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
        
        const res = await axios.get(url, { headers: getHeaders() });
        const allSkus = res.data.items || res.data || [];
        
        const data = [['Bidso SKU ID', 'Vertical Code', 'Vertical Name', 'Model Code', 'Model Name', 'Is Licensed', 'License Code', 'Name', 'Description', 'Status']];
        allSkus.forEach(sku => {
          const vert = verticals.find(v => v.id === sku.vertical_id);
          const mod = models.find(m => m.id === sku.model_id);
          data.push([
            sku.bidso_sku_id || '',
            vert?.code || sku.vertical_code || '',
            vert?.name || '',
            mod?.code || sku.model_code || '',
            mod?.name || '',
            sku.is_licensed ? 'TRUE' : 'FALSE',
            sku.license_code || '',
            sku.name || '',
            sku.description || '',
            sku.status || 'ACTIVE'
          ]);
        });
        
        const ws = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = [{ wch: 22 }, { wch: 10 }, { wch: 20 }, { wch: 10 }, { wch: 20 }, { wch: 12 }, { wch: 14 }, { wch: 30 }, { wch: 40 }, { wch: 10 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Bidso SKUs');
        XLSX.writeFile(wb, `bidso_skus_${new Date().toISOString().slice(0,10)}.xlsx`);
        toast.success(`Downloaded ${allSkus.length} Bidso SKUs`);
        
      } else {
        // Fetch ALL filtered Buyer SKUs
        let url = `${API}/sku-management/buyer-skus?page=1&page_size=10000`;
        if (filterVertical) url += `&vertical_id=${filterVertical}`;
        if (filterBrand) url += `&brand_id=${filterBrand}`;
        if (filterModel) {
          const selectedModel = models.find(m => m.id === filterModel);
          url += `&model_code=${encodeURIComponent(selectedModel.code)}`;
        }
        if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
        
        const res = await axios.get(url, { headers: getHeaders() });
        const allSkus = res.data.items || res.data || [];
        
        const data = [['Buyer SKU ID', 'Bidso SKU ID', 'Brand Code', 'Brand Name', 'Vertical', 'Model', 'Name', 'HSN Code', 'GST Rate', 'Status']];
        allSkus.forEach(sku => {
          const brand = brands.find(b => b.id === sku.brand_id);
          const vert = verticals.find(v => v.id === sku.vertical_id);
          const mod = models.find(m => m.id === sku.model_id);
          data.push([
            sku.buyer_sku_id || sku.sku_id || '',
            sku.bidso_sku_id || '',
            brand?.code || sku.brand_code || '',
            brand?.name || '',
            vert?.name || '',
            mod?.name || '',
            sku.name || sku.description || '',
            sku.hsn_code || '',
            sku.gst_rate || 18,
            sku.status || 'ACTIVE'
          ]);
        });
        
        const ws = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = [{ wch: 25 }, { wch: 20 }, { wch: 10 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 12 }, { wch: 10 }, { wch: 10 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Buyer SKUs');
        XLSX.writeFile(wb, `buyer_skus_${new Date().toISOString().slice(0,10)}.xlsx`);
        toast.success(`Downloaded ${allSkus.length} Buyer SKUs`);
      }
    } catch (error) {
      toast.error("Failed to download SKUs");
      console.error(error);
    } finally {
      setExportLoading(false);
    }
  };

  // Data Sync Functions
  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/demand-hub/export-all-sku-data/download`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sku_data_export_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
    setExportLoading(false);
  };

  const handleImportData = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setSyncLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/demand-hub/import-sku-data/upload`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      
      const results = response.data.results;
      const summary = Object.entries(results)
        .map(([key, val]) => `${key}: ${val.imported} imported, ${val.skipped} skipped`)
        .join('\n');
      
      toast.success('Data imported successfully', { description: summary });
      setShowSyncDialog(false);
      fetchMasterData();
      fetchBidsoSKUs();
      fetchBuyerSKUs();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import data');
    }
    setSyncLoading(false);
    event.target.value = '';
  };

  // ---- Bulk Bidso SKU Import (preserves caller-supplied bidso_sku_id) ----
  // The backend POST /sku-management/bidso-skus/bulk-import endpoint takes
  // each row's BIDSO_SKU_ID verbatim, parses vertical/model from the id (or
  // explicit columns), and SKIPs existing ids. No new code is minted.
  const downloadBidsoImportTemplate = () => {
    const headers = [
      'BIDSO_SKU_ID',     // e.g. KS_PE_001 — preserved as-is
      'VERTICAL_CODE',    // optional — auto-parsed from id if blank
      'MODEL_CODE',       // optional — auto-parsed from id if blank
      'NUMERIC_CODE',     // optional — auto-parsed from id if blank
      'NAME',             // optional — defaults to "<vertical> - <model>"
      'DESCRIPTION',      // optional
      'IS_LICENSED',      // optional — TRUE/FALSE; defaults to FALSE
      'LICENSE_CODE',     // optional — required only when IS_LICENSED=TRUE
    ];
    const sampleRow = ['KS_PE_001', 'KS', 'PE', '001', 'Sample Kids Scooter', '', 'FALSE', ''];
    const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
    ws['!cols'] = headers.map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bidso SKUs');
    const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([out], { type: 'application/octet-stream' }), 'bidso_skus_bulk_import_template.xlsx');
    toast.success('Template downloaded');
  };

  const handleBidsoBulkImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBidsoImportLoading(true);
    setBidsoImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await axios.post(
        `${API}/sku-management/bidso-skus/bulk-import`,
        formData,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      const res = r.data?.results || r.data;
      setBidsoImportResult(res);
      const created = res?.created ?? 0;
      const skipped = res?.skipped ?? 0;
      const errors = (res?.errors || []).length;
      if (created > 0) {
        toast.success(`Imported ${created} Bidso SKU(s) (${skipped} skipped, ${errors} errors)`, { duration: 7000 });
      } else if (errors > 0) {
        toast.error(`0 created. ${errors} error(s) — check the results below.`, { duration: 9000 });
      } else {
        toast.warning(`Nothing new created — all ${skipped} row(s) already exist.`, { duration: 7000 });
      }
      fetchBidsoSKUs();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk import failed');
    } finally {
      setBidsoImportLoading(false);
      if (event.target) event.target.value = '';
    }
  };

  // BOM Upload Functions
  const handleDownloadBOMTemplate = async () => {
    try {
      const response = await axios.get(`${API}/sku-management/bom/bulk-upload/template`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'BOM_Upload_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBOMUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setBomUploadLoading(true);
    setBomUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const data = await uploadWithReceipt(
        `${API}/sku-management/bom/bulk-upload?mode=${bomUploadMode}`,
        formData,
        {
          axiosConfig: { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } },
          showToast: false,
        }
      );

      setBomUploadResult(data);

      // Show appropriate toast based on success status
      const summary = data.summary || {};
      if (data.success === false) {
        toast.error('BOM upload failed — see receipt for line-by-line details.');
      } else if (data.errors?.length > 0) {
        toast.warning(`BOM upload completed with ${data.errors.length} error(s) — Receipt downloaded.`);
      } else {
        const modeLabel = bomUploadMode === 'overwrite' ? 'replaced' : 'merged';
        toast.success(`BOM upload completed — items ${modeLabel}. Receipt downloaded.`);
      }
      if (summary.total) {
        toast.info(`${summary.success} of ${summary.total} rows succeeded.`, { duration: 5000 });
      }

      fetchBidsoSKUs();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload BOM');
      if (error.response?.data) {
        setBomUploadResult(error.response.data);
      }
    }
    
    setBomUploadLoading(false);
    event.target.value = '';
  };

  const [bomExportLoading, setBomExportLoading] = useState(false);

  const handleExportBOM = async () => {
    setBomExportLoading(true);
    toast.info('Generating BOM export... This may take a few seconds.');
    try {
      const response = await axios.get(`${API}/sku-management/bom/export`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      
      // Use file-saver for reliable download with correct MIME type
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      saveAs(blob, `BOM_Export_${new Date().toISOString().slice(0,10)}.xlsx`);
      
      toast.success('BOM data exported successfully!');
    } catch (error) {
      console.error('BOM export error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to export BOM';
      toast.error(errorMsg);
    } finally {
      setBomExportLoading(false);
    }
  };

  // Data Integrity Handlers
  const handleCheckIntegrity = async () => {
    setIntegrityLoading(true);
    setIntegrityData(null);
    try {
      const response = await axios.get(`${API}/sku-management/bidso-skus/missing-from-buyer-skus`, {
        headers: getHeaders()
      });
      setIntegrityData(response.data);
    } catch (error) {
      toast.error('Failed to check data integrity');
    } finally {
      setIntegrityLoading(false);
    }
  };

  const handleRepairMissingBidsoSKUs = async () => {
    if (!window.confirm(`This will create ${integrityData?.total_missing || 0} missing Bidso SKUs. Continue?`)) {
      return;
    }
    
    setRepairLoading(true);
    try {
      const response = await axios.post(`${API}/sku-management/bidso-skus/auto-create-from-buyer-skus`, {}, {
        headers: getHeaders()
      });
      toast.success(`Created ${response.data.created} Bidso SKUs`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} items could not be created`);
      }
      // Refresh the check
      handleCheckIntegrity();
      // Refresh stats
      fetchStats();
    } catch (error) {
      toast.error('Failed to repair missing Bidso SKUs');
    } finally {
      setRepairLoading(false);
    }
  };

  // Bulk Delete Handlers
  const handleBulkDeleteFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setBulkDeleteFile(file);
    setBulkDeleteLoading(true);
    setBulkDeletePreview(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/sku-management/buyer-skus/bulk-delete/preview`, formData, {
        headers: getHeaders()
      });
      
      setBulkDeletePreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview delete file');
      setBulkDeleteFile(null);
    }
    setBulkDeleteLoading(false);
    e.target.value = null;
  };

  const handleConfirmBulkDelete = async () => {
    if (!bulkDeleteFile) return;
    
    setBulkDeleteLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', bulkDeleteFile);
      
      const response = await axios.post(`${API}/sku-management/buyer-skus/bulk-delete/confirm`, formData, {
        headers: getHeaders()
      });
      
      toast.success(`Deleted ${response.data.deleted} Buyer SKUs`);
      setShowBulkDeleteDialog(false);
      setBulkDeletePreview(null);
      setBulkDeleteFile(null);
      fetchBuyerSKUs();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
    setBulkDeleteLoading(false);
  };

  const closeBulkDeleteDialog = () => {
    setShowBulkDeleteDialog(false);
    setBulkDeletePreview(null);
    setBulkDeleteFile(null);
  };

  return (
    <div className="p-6 space-y-6" data-testid="sku-management-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SKU Management</h1>
          <p className="text-gray-500 mt-1">Manage Bidso SKUs (base products) and Buyer SKUs (branded variants)</p>
        </div>
        <div className="flex gap-2">
          {/* SKU Bulk Import */}
          <Button 
            variant="outline" 
            onClick={() => window.open(`${API}/sku-management/skus/bulk-import/template`, '_blank')}
            data-testid="sku-import-template-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            SKU Template
          </Button>
          <div className="relative">
            <Button 
              variant="default"
              onClick={() => skuImportFileRef.current?.click()}
              disabled={skuImportLoading}
              data-testid="sku-import-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {skuImportLoading ? "Importing..." : "Import SKUs"}
            </Button>
            <input
              ref={skuImportFileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                
                setSkuImportLoading(true);
                const formData = new FormData();
                formData.append('file', file);
                
                try {
                  const data = await uploadWithReceipt(`${API}/sku-management/skus/bulk-import`, formData, {
                    axiosConfig: { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } },
                    filename: 'sku_import_result.xlsx',
                    showToast: false,
                  });
                  
                  // Show summary
                  const created = data.created || 0;
                  const skipped = data.skipped || 0;
                  const errors = data.errors || 0;
                  if (created > 0) {
                    toast.success(`Imported ${created} SKUs. ${skipped} skipped, ${errors} errors. Check downloaded file.`);
                    fetchBidsoSKUs();
                    fetchBuyerSKUs();
                    fetchStats();
                  } else if (skipped > 0) {
                    toast.warning(`All ${skipped} SKUs already exist. No new imports.`);
                  } else {
                    toast.error(`Import failed with ${errors} errors. Check downloaded file.`);
                  }
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Failed to import SKUs");
                }
                setSkuImportLoading(false);
                e.target.value = '';
              }}
            />
          </div>
          <PermButton entity="BOM" action="UPDATE" variant="outline" onClick={() => setShowBOMUploadDialog(true)} data-testid="bom-upload-btn">
            <Upload className="h-4 w-4 mr-2" />
            BOM Upload
          </PermButton>
          <Button variant="outline" onClick={() => setShowSyncDialog(true)} data-testid="sync-data-btn">
            <Database className="h-4 w-4 mr-2" />
            Data Sync
          </Button>
          <Button variant="outline" onClick={() => { setShowIntegrityDialog(true); handleCheckIntegrity(); }} data-testid="integrity-check-btn">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Data Integrity
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Bidso SKUs</p>
                <p className="text-2xl font-bold">{stats.bidso_skus}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Layers className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Buyer SKUs</p>
                <p className="text-2xl font-bold">{stats.buyer_skus}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileSpreadsheet className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Common BOMs</p>
                <p className="text-2xl font-bold">{stats.common_boms}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Layers className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Brands</p>
                <p className="text-2xl font-bold">{brands.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="bidso" data-testid="bidso-tab">
            <Package className="h-4 w-4 mr-2" />
            Bidso SKUs (Base Products)
          </TabsTrigger>
          <TabsTrigger value="buyer" data-testid="buyer-tab">
            <Layers className="h-4 w-4 mr-2" />
            Buyer SKUs (Branded Variants)
          </TabsTrigger>
        </TabsList>

        {/* Bidso SKUs Tab */}
        <TabsContent value="bidso" className="space-y-4">
          {/* Filters & Actions */}
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-3 flex-wrap">
              <Select value={filterVertical} onValueChange={(v) => { handleFilterVerticalChange(v === "all" ? "" : v); setFilterModel(""); }}>
                <SelectTrigger className="w-[180px]" data-testid="filter-vertical">
                  <SelectValue placeholder="All Verticals" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Verticals</SelectItem>
                  {verticals.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.code} - {v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterModel} onValueChange={(v) => handleFilterModelChange(v === "all" ? "" : v)} disabled={!filterVertical}>
                <SelectTrigger className="w-[180px]" data-testid="filter-model">
                  <SelectValue placeholder="All Models" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Models</SelectItem>
                  {getFilteredModels().map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.code} - {m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search SKU ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 w-[200px]"
                  data-testid="search-input"
                />
              </div>
              
              <Button variant="outline" onClick={handleSearch}>
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleDownloadSKUs("bidso")} disabled={exportLoading || bidsoTotal === 0}>
                <Download className="h-4 w-4 mr-2" />
                {exportLoading ? "..." : `Download All (${bidsoTotal})`}
              </Button>
              {/* Feb 2026: "Create Bidso SKU" UI removed (was minting half-baked
                  SKUs). Single-row creation now goes through Colourway Composer's
                  clone-as-variant. For PRE-EXISTING Bidso SKU IDs that need to
                  land in the system AS-IS, the bulk-import button below calls
                  POST /sku-management/bidso-skus/bulk-import which preserves the
                  caller-supplied bidso_sku_id and skips duplicates. */}
              <Button
                onClick={() => setCreateBidsoOpen(true)}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="open-bidso-composer-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Bidso SKU
              </Button>
              <Button
                variant="outline"
                onClick={downloadBidsoImportTemplate}
                data-testid="bidso-import-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Template
              </Button>
              <Button
                onClick={() => bidsoImportFileRef.current?.click()}
                disabled={bidsoImportLoading}
                data-testid="bidso-bulk-import-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                {bidsoImportLoading ? "Importing…" : "Bulk Import Bidso SKUs"}
              </Button>
              <input
                ref={bidsoImportFileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleBidsoBulkImport}
                data-testid="bidso-bulk-import-input"
              />
            </div>
          </div>

          {/* Bulk import result summary */}
          {bidsoImportResult && (
            <div className="border rounded-md p-3 bg-slate-50 text-sm space-y-2" data-testid="bidso-import-result">
              <div className="flex items-center justify-between">
                <div className="font-semibold">Bulk Import Result</div>
                <button
                  type="button"
                  className="text-xs text-stone-500 hover:text-stone-800"
                  onClick={() => setBidsoImportResult(null)}
                >
                  Dismiss
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="rounded bg-white border p-2">
                  <div className="text-stone-500">Processed</div>
                  <div className="text-lg font-bold">{bidsoImportResult.processed ?? 0}</div>
                </div>
                <div className="rounded bg-emerald-50 border border-emerald-200 p-2">
                  <div className="text-emerald-700">Created</div>
                  <div className="text-lg font-bold text-emerald-800">{bidsoImportResult.created ?? 0}</div>
                </div>
                <div className="rounded bg-amber-50 border border-amber-200 p-2">
                  <div className="text-amber-700">Skipped (already exist)</div>
                  <div className="text-lg font-bold text-amber-800">{bidsoImportResult.skipped ?? 0}</div>
                </div>
                <div className="rounded bg-rose-50 border border-rose-200 p-2">
                  <div className="text-rose-700">Errors</div>
                  <div className="text-lg font-bold text-rose-800">{(bidsoImportResult.errors || []).length}</div>
                </div>
              </div>
              {(bidsoImportResult.errors || []).length > 0 && (
                <div className="mt-2">
                  <div className="text-[11px] font-semibold text-rose-700 mb-1">Errors</div>
                  <div className="border rounded bg-white max-h-40 overflow-y-auto text-[11px] font-mono p-2">
                    {bidsoImportResult.errors.slice(0, 50).map((err, i) => (
                      <div key={i} className="border-b last:border-b-0 py-0.5">{err}</div>
                    ))}
                    {bidsoImportResult.errors.length > 50 && (
                      <div className="text-stone-500 italic">…and {bidsoImportResult.errors.length - 50} more</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bidso SKUs Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bidso SKU ID</TableHead>
                    <TableHead>Vertical</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Buyer SKUs</TableHead>
                    <TableHead>BOM Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : bidsoSKUs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No Bidso SKUs found. Create your first one!
                      </TableCell>
                    </TableRow>
                  ) : (
                    bidsoSKUs.map(sku => (
                      <TableRow key={sku.id} data-testid={`bidso-row-${sku.bidso_sku_id}`}>
                        <TableCell>
                          <span className="font-mono font-medium text-blue-600">{sku.bidso_sku_id}</span>
                        </TableCell>
                        <TableCell>{sku.vertical_name || sku.vertical_code}</TableCell>
                        <TableCell>{sku.model_name || sku.model_code}</TableCell>
                        <TableCell>{sku.name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{sku.buyer_sku_count || 0} variants</Badge>
                        </TableCell>
                        <TableCell>
                          {sku.has_bom ? (
                            sku.bom_locked ? (
                              <Badge className="bg-green-100 text-green-700">
                                <Lock className="h-3 w-3 mr-1" />
                                Locked
                              </Badge>
                            ) : (
                              <Badge variant="outline">BOM Set</Badge>
                            )
                          ) : (
                            <Badge variant="outline" className="text-gray-400">No BOM</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleViewBOM(sku)}
                              data-testid={`view-bom-${sku.bidso_sku_id}`}
                            >
                              <FileSpreadsheet className="h-4 w-4" />
                            </Button>
                            <PermButton
                              entity="SKUAsset" action="READ"
                              variant="ghost"
                              size="sm"
                              title="Image Assets"
                              onClick={() => setAssetsTarget({ level: "BIDSO", skuId: sku.bidso_sku_id })}
                              data-testid={`image-assets-bidso-${sku.bidso_sku_id}`}
                            >
                              <Images className="h-4 w-4" />
                            </PermButton>
                            <PermButton
                              entity="BuyerSKU" action="CREATE"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedBidso(sku);
                                setBuyerForm({ ...buyerForm, bidso_sku_id: sku.bidso_sku_id });
                                setShowBuyerDialog(true);
                              }}
                              data-testid={`add-buyer-${sku.bidso_sku_id}`}
                            >
                              <Plus className="h-4 w-4" />
                            </PermButton>
                            <PermButton
                              entity="BuyerSKU" action="CREATE"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedBidso(sku);
                                setShowBulkCreateDialog(true);
                              }}
                              data-testid={`bulk-create-${sku.bidso_sku_id}`}
                            >
                              <Copy className="h-4 w-4" />
                            </PermButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {/* Pagination for Bidso SKUs */}
              <Pagination
                currentPage={bidsoPage}
                totalPages={bidsoTotalPages}
                totalItems={bidsoTotal}
                pageSize={bidsoPageSize}
                onPageChange={handleBidsoPageChange}
                onPageSizeChange={handleBidsoPageSizeChange}
                loading={loading}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Buyer SKUs Tab */}
        <TabsContent value="buyer" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-3 flex-wrap">
              <Select value={filterBrand} onValueChange={(v) => handleFilterBrandChange(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]" data-testid="filter-brand">
                  <SelectValue placeholder="All Brands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <BuyerSkuSearchBox
                value={searchQuery}
                onSubmit={(q) => {
                  setSearchQuery(q);
                  handleSearch();
                }}
                onClear={() => {
                  setSearchQuery("");
                  // Trigger a page reset + refetch with cleared query.
                  setBuyerPage(1);
                  fetchBuyerSKUs();
                }}
                testId="buyer-sku-search"
              />
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleDownloadSKUs("buyer")} disabled={exportLoading || buyerTotal === 0}>
                <Download className="h-4 w-4 mr-2" />
                {exportLoading ? "..." : `Download All (${buyerTotal})`}
              </Button>
              <PermButton
                entity="BuyerSKU" action="DELETE"
                variant="outline" 
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setShowBulkDeleteDialog(true)}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Bulk Delete
              </PermButton>
              <PermButton
                entity="BuyerSKU" action="UPDATE"
                variant="outline"
                onClick={() => setShowBulkHSNDialog(true)}
                data-testid="bulk-hsn-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Bulk HSN/GST
              </PermButton>
            </div>
          </div>

          {/* Buyer SKUs Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Buyer SKU ID</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>HSN Code</TableHead>
                    <TableHead>GST %</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : buyerSKUs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No Buyer SKUs found. Create from a Bidso SKU first.
                      </TableCell>
                    </TableRow>
                  ) : (
                    buyerSKUs.map(sku => {
                      return (
                      <TableRow key={sku.id} data-testid={`buyer-row-${sku.buyer_sku_id}`}>
                        <TableCell>
                          <span className="font-mono font-medium text-green-600">{sku.buyer_sku_id}</span>
                        </TableCell>
                        <TableCell>
                          <Badge>{sku.brand_code}</Badge>
                          <span className="ml-2 text-sm text-gray-500">{sku.brand_name}</span>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={sku.name}>{sku.name}</TableCell>
                        <TableCell>
                          {sku.hsn_code ? (
                            <span className="font-mono text-sm">{sku.hsn_code}</span>
                          ) : (
                            <span className="text-amber-500 text-xs">Not set</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {sku.gst_rate ? (
                            <Badge variant="outline">{sku.gst_rate}%</Badge>
                          ) : (
                            <span className="text-amber-500 text-xs">Not set</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={sku.status === 'ACTIVE' ? 'default' : 'secondary'}>
                            {sku.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleViewBuyerBOM(sku)}
                              data-testid={`view-bom-${sku.buyer_sku_id}`}
                              title="View Full BOM"
                            >
                              <FileSpreadsheet className="h-4 w-4" />
                            </Button>
                            <PermButton
                              entity="SKUAsset" action="READ"
                              variant="ghost"
                              size="sm"
                              title="Image Assets"
                              onClick={() => setAssetsTarget({ level: "BUYER", skuId: sku.buyer_sku_id })}
                              data-testid={`image-assets-buyer-${sku.buyer_sku_id}`}
                            >
                              <Images className="h-4 w-4" />
                            </PermButton>
                            <PermButton
                              entity="BuyerSKU" action="UPDATE"
                              variant="ghost" 
                              size="sm" 
                              title="Edit HSN/GST"
                              onClick={() => handleEditBuyerSKU(sku)}
                              data-testid={`edit-hsn-${sku.buyer_sku_id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </PermButton>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Upload Tech Pack Images"
                              onClick={() => openTechpackDialog(sku)}
                              data-testid={`techpack-upload-${sku.buyer_sku_id}`}
                            >
                              <ImageIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Download Tech Pack PDF"
                              onClick={() => downloadTechpack(sku)}
                              data-testid={`techpack-download-${sku.buyer_sku_id}`}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              {/* Pagination for Buyer SKUs */}
              <Pagination
                currentPage={buyerPage}
                totalPages={buyerTotalPages}
                totalItems={buyerTotal}
                pageSize={buyerPageSize}
                onPageChange={handleBuyerPageChange}
                onPageSizeChange={handleBuyerPageSizeChange}
                loading={loading}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Bidso SKU Dialog (Composer Path 1 step 1 — Feb 2026) */}
      <Dialog open={createBidsoOpen} onOpenChange={setCreateBidsoOpen}>
        <DialogContent className="max-w-lg" data-testid="create-bidso-dialog">
          <DialogHeader>
            <DialogTitle>Create Bidso SKU</DialogTitle>
            <DialogDescription>
              An atomic numeric counter assigns the next code for your (vertical, model) pair. The new SKU lands in <span className="font-mono">DRAFT_NO_BOM</span> — attach the BOM from the Colourway Composer tab.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-600">Vertical</Label>
                <Select
                  value={createBidsoForm.vertical_id}
                  onValueChange={(v) => setCreateBidsoForm((f) => ({ ...f, vertical_id: v, model_id: "" }))}
                >
                  <SelectTrigger data-testid="create-bidso-vertical"><SelectValue placeholder="Pick…"/></SelectTrigger>
                  <SelectContent>
                    {(verticals || []).filter((v) => (v.status || "ACTIVE") === "ACTIVE").map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.code} — {v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-600">Model</Label>
                <Select
                  value={createBidsoForm.model_id}
                  onValueChange={(v) => setCreateBidsoForm((f) => ({ ...f, model_id: v }))}
                  disabled={!createBidsoForm.vertical_id}
                >
                  <SelectTrigger data-testid="create-bidso-model">
                    <SelectValue placeholder={createBidsoForm.vertical_id ? "Pick…" : "Pick vertical first"}/>
                  </SelectTrigger>
                  <SelectContent>
                    {(models || [])
                      .filter((m) => !createBidsoForm.vertical_id || m.vertical_id === createBidsoForm.vertical_id)
                      .map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.code} — {m.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-600">License (optional)</Label>
              <Select
                value={createBidsoForm.license_code || "__none__"}
                onValueChange={(v) => setCreateBidsoForm((f) => ({ ...f, license_code: v === "__none__" ? "" : v }))}
              >
                <SelectTrigger data-testid="create-bidso-license">
                  <SelectValue placeholder="(unlicensed)"/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">(unlicensed)</SelectItem>
                  {licensesList.map((lic) => (
                    <SelectItem key={lic.id || lic.code} value={lic.code}>
                      {lic.code} — {lic.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-600">Display name (optional)</Label>
              <Input
                value={createBidsoForm.name}
                onChange={(e) => setCreateBidsoForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="What this SKU is internally called"
                data-testid="create-bidso-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateBidsoOpen(false)}>Cancel</Button>
            <Button
              onClick={submitCreateBidso}
              disabled={creatingBidso || !createBidsoForm.vertical_id || !createBidsoForm.model_id}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="create-bidso-submit"
            >
              {creatingBidso ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Plus className="w-4 h-4 mr-2"/>}
              Generate code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Buyer SKU Dialog */}
      <Dialog open={showBuyerDialog} onOpenChange={setShowBuyerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Buyer SKU</DialogTitle>
            <DialogDescription>
              Create a branded variant of {selectedBidso?.bidso_sku_id || "a Bidso SKU"}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {selectedBidso && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Parent Bidso SKU:</p>
                <p className="font-mono font-bold text-blue-600">{selectedBidso.bidso_sku_id}</p>
                <p className="text-sm text-gray-500">{selectedBidso.name}</p>
              </div>
            )}
            
            <div>
              <Label>Brand *</Label>
              <Select 
                value={buyerForm.brand_id} 
                onValueChange={(v) => setBuyerForm({ ...buyerForm, brand_id: v })}
              >
                <SelectTrigger data-testid="buyer-brand-select">
                  <SelectValue placeholder="Select Brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {buyerForm.brand_id && selectedBidso && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">Buyer SKU ID will be:</p>
                <p className="text-lg font-mono font-bold text-green-600">
                  {brands.find(b => b.id === buyerForm.brand_id)?.code}_{selectedBidso.bidso_sku_id}
                </p>
              </div>
            )}
            
            <div>
              <Label>Name (optional)</Label>
              <Input 
                value={buyerForm.name}
                onChange={(e) => setBuyerForm({ ...buyerForm, name: e.target.value })}
                placeholder="Auto-generated from brand + product"
              />
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowBuyerDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateBuyer}
                disabled={!buyerForm.brand_id || !buyerForm.bidso_sku_id}
                data-testid="create-buyer-submit"
              >
                Create Buyer SKU
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Create Buyer SKUs Dialog */}
      <Dialog open={showBulkCreateDialog} onOpenChange={setShowBulkCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Create Buyer SKUs</DialogTitle>
            <DialogDescription>
              Create Buyer SKUs for multiple brands from {selectedBidso?.bidso_sku_id}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">Select brands to create Buyer SKUs:</p>
            
            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {brands.map(brand => (
                <label 
                  key={brand.id} 
                  className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer"
                >
                  <input 
                    type="checkbox" 
                    id={`brand-${brand.id}`}
                    className="rounded"
                    data-brand-id={brand.id}
                  />
                  <span className="font-mono text-sm">{brand.code}</span>
                  <span className="text-sm text-gray-500">{brand.name}</span>
                </label>
              ))}
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowBulkCreateDialog(false)}>Cancel</Button>
              <Button 
                onClick={() => {
                  const selectedBrands = Array.from(document.querySelectorAll('[data-brand-id]:checked'))
                    .map(el => el.dataset.brandId);
                  handleBulkCreateBuyerSKUs(selectedBrands);
                }}
                data-testid="bulk-create-submit"
              >
                Create Selected
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View BOM Dialog */}
      <Dialog open={showBOMDialog} onOpenChange={setShowBOMDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Common BOM - {selectedBidso?.bidso_sku_id}</DialogTitle>
            <DialogDescription>
              This BOM is shared by all Buyer SKU variants of this Bidso SKU.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {bomLocked ? (
                  <Badge className="bg-green-100 text-green-700">
                    <Lock className="h-3 w-3 mr-1" />
                    BOM Locked
                  </Badge>
                ) : (
                  <Badge variant="outline">BOM Editable</Badge>
                )}
              </div>
              
              <div className="flex gap-2">
                {bomLocked ? (
                  <Button variant="outline" size="sm" onClick={handleUnlockBOM}>
                    <Unlock className="h-4 w-4 mr-2" />
                    Unlock BOM
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={handleLockBOM} disabled={commonBOM.length === 0}>
                    <Lock className="h-4 w-4 mr-2" />
                    Lock BOM
                  </Button>
                )}
              </div>
            </div>
            
            <Separator />
            
            {commonBOM.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileSpreadsheet className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No BOM items defined yet.</p>
                <p className="text-sm">Add raw materials to define the common BOM.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>RM ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commonBOM.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono">{item.rm_id}</TableCell>
                      <TableCell>{item.rm_name || "-"}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.unit}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <SKUAssetsDialog
        open={Boolean(assetsTarget)}
        onClose={() => setAssetsTarget(null)}
        level={assetsTarget?.level}
        skuId={assetsTarget?.skuId}
        canCreate={hasPermission("SKUAsset", "CREATE")}
        canUpdate={hasPermission("SKUAsset", "UPDATE")}
        getHeaders={getHeaders}
      />

      {/* View Buyer SKU Full BOM Dialog */}
      <Dialog open={showBuyerBOMDialog} onOpenChange={setShowBuyerBOMDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Full BOM - {selectedBuyerSKU?.buyer_sku_id}
            </DialogTitle>
            <DialogDescription>
              Complete Bill of Materials for this Buyer SKU (Common + Brand-Specific)
            </DialogDescription>
          </DialogHeader>

          {/* BOM Approval Panel (Feb 2026 — dual-track sign-off).
              Feb 2026 widen: `_canReadBomApproval()` is included so
              read-only viewers (e.g. TECH_OPS_ENGINEER) see the panel
              on pending SKUs — the case they most need to follow up
              on. The old disjuncts remain as belt-and-suspenders for
              the login-first-tick before permissions land. */}
          {bomApproval && (_canReadBomApproval() || _canApproveSupply() || _canApproveDemand() || bomApproval.supply_approval_status === "APPROVED" || bomApproval.demand_approval_status === "APPROVED") && (
            <div
              className={`border rounded-lg p-4 mt-2 ${bomApproval.bom_locked ? "border-green-300 bg-green-50/50" : "border-blue-200 bg-blue-50/40"}`}
              data-testid="bom-approval-panel"
            >
              <div className="flex items-center justify-between mb-3">
                {bomApproval.bom_locked ? (
                  <Badge className="bg-green-100 text-green-700">
                    <Lock className="h-3 w-3 mr-1" />
                    BOM Locked (Both Approved)
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-blue-700 border-blue-300">
                    Dual-Track Approval
                  </Badge>
                )}
                {bomApproval.bom_locked && _isMasterAdmin() && (
                  <Button variant="outline" size="sm" onClick={handleBomUnlock} disabled={approvalActionLoading} data-testid="bom-unlock-btn">
                    <Unlock className="h-4 w-4 mr-2" />Unlock (MA only)
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded p-3 border">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Supply Approval</p>
                  {bomApproval.supply_approval_status === "APPROVED" ? (
                    <div className="text-sm">
                      <span className="text-green-700 font-medium">Approved</span>
                      <div className="text-xs text-gray-600 mt-1" data-testid="supply-approved-by">
                        by <b>{bomApproval.supply_approved_by_name || "—"}</b> on {bomApproval.supply_approved_at ? new Date(bomApproval.supply_approved_at).toLocaleDateString() : "—"}
                      </div>
                    </div>
                  ) : _canApproveSupply() ? (
                    // Demand-before-Supply sequencing (Jul 2026): Supply
                    // is blocked until Demand approves, EXCEPT for
                    // MASTER_ADMIN (matches the backend exemption in
                    // `_record_approval`). Button renders visibly
                    // disabled — not hidden — so it's obvious the action
                    // exists and why it's blocked. The dialog's
                    // `setBomApproval(res.data.approval)` reactive
                    // update after a Demand approve call flips this
                    // predicate automatically — no manual reload needed.
                    (() => {
                      const demandApproved = bomApproval.demand_approval_status === "APPROVED";
                      const isMA = _isMasterAdmin();
                      const blocked = !demandApproved && !isMA;
                      return (
                        <div
                          title={blocked ? "Will be enabled once Demand approval happens." : ""}
                          data-testid={blocked ? "bom-supply-blocked-tooltip" : undefined}
                        >
                          <Button
                            size="sm"
                            onClick={() => handleBomApprove("supply")}
                            disabled={approvalActionLoading || blocked}
                            data-testid="bom-supply-approve-btn"
                          >
                            Supply Approval
                          </Button>
                        </div>
                      );
                    })()
                  ) : (
                    <span className="text-xs text-gray-400 italic">Pending Supply team review</span>
                  )}
                </div>
                <div className="bg-white rounded p-3 border">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Demand Approval</p>
                  {bomApproval.demand_approval_status === "APPROVED" ? (
                    <div className="text-sm">
                      <span className="text-green-700 font-medium">Approved</span>
                      <div className="text-xs text-gray-600 mt-1" data-testid="demand-approved-by">
                        by <b>{bomApproval.demand_approved_by_name || "—"}</b> on {bomApproval.demand_approved_at ? new Date(bomApproval.demand_approved_at).toLocaleDateString() : "—"}
                      </div>
                    </div>
                  ) : _canApproveDemand() ? (
                    <Button size="sm" onClick={() => handleBomApprove("demand")} disabled={approvalActionLoading} data-testid="bom-demand-approve-btn">Demand Approval</Button>
                  ) : (
                    <span className="text-xs text-gray-400 italic">Pending Demand Planner review</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {buyerBOMLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : buyerFullBOM ? (
            <div className="flex-1 overflow-y-auto space-y-6 mt-4 pr-2">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">{buyerFullBOM.total_items?.length || 0}</p>
                  <p className="text-xs text-blue-600">Total Items</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-700">{buyerFullBOM.common_items?.length || 0}</p>
                  <p className="text-xs text-gray-600">Common Items</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{buyerFullBOM.brand_specific_items?.length || 0}</p>
                  <p className="text-xs text-green-600">Brand-Specific</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <p className="text-sm font-mono font-bold text-purple-700">{buyerFullBOM.bidso_sku_id}</p>
                  <p className="text-xs text-purple-600">Parent Bidso SKU</p>
                </div>
              </div>

              <Separator />
              
              {/* Common BOM Section */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Common BOM (from {buyerFullBOM.bidso_sku_id})
                  {buyerFullBOM.is_common_bom_locked && (
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      <Lock className="h-3 w-3 mr-1" />
                      Locked
                    </Badge>
                  )}
                </h4>
                {buyerFullBOM.common_items?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="w-[120px]">RM ID</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-[80px] text-right">Qty</TableHead>
                          <TableHead className="w-[60px]">Unit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {buyerFullBOM.common_items.map((item, idx) => (
                          <TableRow key={`common-${idx}`}>
                            <TableCell className="font-mono text-sm">{item.rm_id}</TableCell>
                            <TableCell className="text-sm text-gray-600">{item.rm_name || "-"}</TableCell>
                            <TableCell className="text-right font-medium">{item.quantity}</TableCell>
                            <TableCell className="text-sm text-gray-500">{item.unit}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No common BOM items defined for the parent Bidso SKU.</p>
                )}
              </div>
              
              {/* Brand-Specific BOM Section */}
              <div>
                <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Brand-Specific BOM ({buyerFullBOM.brand_code})
                  <Badge variant="outline" className="ml-2 text-xs">Editable</Badge>
                </h4>
                {buyerFullBOM.brand_specific_items?.length > 0 ? (
                  <div className="border border-green-200 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-green-50">
                          <TableHead className="w-[120px]">RM ID</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-[80px] text-right">Qty</TableHead>
                          <TableHead className="w-[60px]">Unit</TableHead>
                          <TableHead className="w-[80px] text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {buyerFullBOM.brand_specific_items.map((item, idx) => (
                          <TableRow key={item.line_id || `brand-${idx}`} className="bg-green-50/30 group">
                            <TableCell className="font-mono text-sm text-green-700">{item.rm_id}</TableCell>
                            <TableCell className="text-sm text-gray-600">{item.rm_name || "-"}</TableCell>
                            <TableCell className="text-right font-medium">{item.quantity}</TableCell>
                            <TableCell className="text-sm text-gray-500">{item.unit}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <PermButton
                                  entity="BOM" action="UPDATE"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleEditBOMItem(item)}
                                  disabled={bomApproval?.bom_locked}
                                  title={bomApproval?.bom_locked
                                    ? "BOM is locked (dual-track approved). Master Admin must unlock before edits."
                                    : "Edit item"}
                                  data-testid={`edit-bom-item-${brandRowTestId(buyerFullBOM.brand_specific_items, idx)}`}
                                >
                                  <Edit className="h-3.5 w-3.5 text-blue-600" />
                                </PermButton>
                                <PermButton
                                  entity="BOM" action="DELETE"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => setPendingDeleteBOMItem(item)}
                                  disabled={bomApproval?.bom_locked}
                                  title={bomApproval?.bom_locked
                                    ? "BOM is locked (dual-track approved). Master Admin must unlock before deletes."
                                    : "Remove item"}
                                  data-testid={`delete-bom-item-${brandRowTestId(buyerFullBOM.brand_specific_items, idx)}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-red-600" />
                                </PermButton>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No brand-specific BOM items defined for this Buyer SKU.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>Failed to load BOM data.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* BOM Item Edit Dialog */}
      <AlertDialog open={!!pendingDeleteBOMItem} onOpenChange={(o) => !o && setPendingDeleteBOMItem(null)}>
        <AlertDialogContent data-testid="delete-bom-item-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove BOM row?</AlertDialogTitle>
            <AlertDialogDescription data-testid="delete-bom-item-confirm-text">
              Remove {pendingDeleteBOMItem?.rm_id} (qty {pendingDeleteBOMItem?.quantity} {pendingDeleteBOMItem?.unit}) from the BOM? Only this row is removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-bom-item-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-bom-item-confirm-btn" onClick={() => handleDeleteBOMItem(pendingDeleteBOMItem)}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showBOMEditDialog} onOpenChange={setShowBOMEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit BOM Item
            </DialogTitle>
            <DialogDescription>
              Modify the brand-specific BOM item for {selectedBuyerSKU?.buyer_sku_id}
            </DialogDescription>
          </DialogHeader>
          
          {scheduleWarning && (
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Production Scheduled!</strong> This SKU is scheduled for production at {scheduleWarning.count} location(s) 
                in the next 10 days ({scheduleWarning.branches.join(", ")}). 
                <br />
                <span className="text-sm">This change will require <strong>Master Admin approval</strong> and notify CPC & Branch Ops.</span>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Current RM ID</Label>
              <Input value={editingBOMItem?.rm_id || ""} disabled className="bg-gray-50 font-mono" />
            </div>
            
            <div>
              <Label>New RM ID (leave same if not changing)</Label>
              <Input 
                value={bomEditForm.new_rm_id} 
                onChange={(e) => setBomEditForm({...bomEditForm, new_rm_id: e.target.value.toUpperCase()})}
                placeholder="e.g., LB_001"
                className="font-mono"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  min="0"
                  value={bomEditForm.quantity} 
                  onChange={(e) => setBomEditForm({...bomEditForm, quantity: e.target.value})}
                />
              </div>
              <div>
                <Label>Unit *</Label>
                <Select value={bomEditForm.unit} onValueChange={(v) => setBomEditForm({...bomEditForm, unit: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PCS">PCS</SelectItem>
                    <SelectItem value="nos">nos</SelectItem>
                    <SelectItem value="KG">KG</SelectItem>
                    <SelectItem value="MTR">MTR</SelectItem>
                    <SelectItem value="SET">SET</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBOMEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveBOMEdit} disabled={bomEditLoading}>
              {bomEditLoading ? "Saving..." : scheduleWarning ? "Submit for Approval" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Data Sync Dialog */}
      <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Data Sync</DialogTitle>
            <DialogDescription>
              Export data from preview or import data to production
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Export Section */}
            <div className="p-4 border rounded-lg bg-blue-50">
              <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                <Download className="h-5 w-5" />
                Export Data (Preview → File)
              </h4>
              <p className="text-sm text-blue-700 mb-3">
                Download all SKU data as a JSON file. Use this on the preview environment.
              </p>
              <Button 
                onClick={handleExportData} 
                disabled={exportLoading}
                className="w-full"
                variant="outline"
              >
                {exportLoading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download Export File
              </Button>
            </div>

            {/* Import Section */}
            <div className="p-4 border rounded-lg bg-green-50">
              <h4 className="font-medium text-green-900 mb-2 flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Import Data (File → Database)
              </h4>
              <p className="text-sm text-green-700 mb-3">
                Upload the exported JSON file to import all SKU data. Use this on production.
              </p>
              <input
                type="file"
                accept=".json"
                ref={syncFileInputRef}
                onChange={handleImportData}
                className="hidden"
              />
              <Button 
                onClick={() => syncFileInputRef.current?.click()} 
                disabled={syncLoading}
                className="w-full"
                variant="outline"
              >
                {syncLoading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Upload Import File
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSyncDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Data Integrity Dialog */}
      <Dialog open={showIntegrityDialog} onOpenChange={setShowIntegrityDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Data Integrity Check
            </DialogTitle>
            <DialogDescription>
              Check for orphaned Buyer SKUs that reference non-existent Bidso SKUs
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {integrityLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Checking data integrity...</span>
              </div>
            ) : integrityData ? (
              <>
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg bg-blue-50 text-center">
                    <p className="text-2xl font-bold text-blue-700">{integrityData.total_referenced}</p>
                    <p className="text-sm text-blue-600">Referenced Bidso SKUs</p>
                  </div>
                  <div className="p-4 border rounded-lg bg-green-50 text-center">
                    <p className="text-2xl font-bold text-green-700">{integrityData.total_existing}</p>
                    <p className="text-sm text-green-600">Existing Bidso SKUs</p>
                  </div>
                  <div className={`p-4 border rounded-lg text-center ${integrityData.total_missing > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                    <p className={`text-2xl font-bold ${integrityData.total_missing > 0 ? 'text-red-700' : 'text-gray-700'}`}>
                      {integrityData.total_missing}
                    </p>
                    <p className={`text-sm ${integrityData.total_missing > 0 ? 'text-red-600' : 'text-gray-600'}`}>Missing Bidso SKUs</p>
                  </div>
                </div>

                {integrityData.total_missing > 0 && (
                  <>
                    {/* Repair Action */}
                    <div className="p-4 border rounded-lg bg-yellow-50">
                      <h4 className="font-medium text-yellow-900 mb-2">Repair Missing Bidso SKUs</h4>
                      <p className="text-sm text-yellow-700 mb-3">
                        Auto-create missing Bidso SKUs by parsing the SKU ID format (VERTICAL_MODEL_NUMBER).
                        This requires that the Vertical and Model codes exist in the system.
                      </p>
                      <Button 
                        onClick={handleRepairMissingBidsoSKUs}
                        disabled={repairLoading}
                        className="w-full"
                        variant="default"
                      >
                        {repairLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Create {integrityData.total_missing} Missing Bidso SKUs
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Missing SKUs List */}
                    <div className="border rounded-lg">
                      <div className="p-3 bg-gray-50 border-b font-medium">
                        Missing Bidso SKUs (showing first 20)
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left">Bidso SKU ID</th>
                              <th className="px-3 py-2 text-left">Vertical</th>
                              <th className="px-3 py-2 text-left">Model</th>
                              <th className="px-3 py-2 text-center">Buyer SKUs</th>
                              <th className="px-3 py-2 text-center">Can Repair?</th>
                            </tr>
                          </thead>
                          <tbody>
                            {integrityData.missing_bidso_skus.slice(0, 20).map((item, idx) => (
                              <tr key={idx} className="border-b hover:bg-gray-50">
                                <td className="px-3 py-2 font-mono text-xs">{item.bidso_sku_id}</td>
                                <td className="px-3 py-2">
                                  <span className={item.vertical_found ? 'text-green-600' : 'text-red-600'}>
                                    {item.vertical_code || '-'}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <span className={item.model_found ? 'text-green-600' : 'text-red-600'}>
                                    {item.model_code || '-'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-center">{item.buyer_sku_count}</td>
                                <td className="px-3 py-2 text-center">
                                  {item.vertical_found && item.model_found ? (
                                    <Check className="h-4 w-4 text-green-600 mx-auto" />
                                  ) : (
                                    <X className="h-4 w-4 text-red-600 mx-auto" />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {integrityData.missing_bidso_skus.length > 20 && (
                        <div className="p-2 bg-gray-50 text-center text-sm text-gray-500">
                          ... and {integrityData.missing_bidso_skus.length - 20} more
                        </div>
                      )}
                    </div>
                  </>
                )}

                {integrityData.total_missing === 0 && (
                  <div className="p-8 text-center bg-green-50 rounded-lg">
                    <Check className="h-12 w-12 text-green-600 mx-auto mb-2" />
                    <p className="text-lg font-medium text-green-700">All Clear!</p>
                    <p className="text-sm text-green-600">All Buyer SKUs have valid Bidso SKU references.</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Click "Check Integrity" to scan for data issues
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIntegrityDialog(false)}>
              Close
            </Button>
            <Button onClick={handleCheckIntegrity} disabled={integrityLoading}>
              {integrityLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Refresh Check
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BOM Upload Dialog */}
      <Dialog open={showBOMUploadDialog} onOpenChange={setShowBOMUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Bulk BOM Upload
            </DialogTitle>
            <DialogDescription>
              Upload Bill of Materials for multiple SKUs at once
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Download Template */}
            <div className="p-4 border rounded-lg bg-gray-50">
              <h4 className="font-medium mb-2">Step 1: Download Template</h4>
              <p className="text-sm text-gray-600 mb-3">
                Get the Excel template with instructions and sample data.
              </p>
              <Button variant="outline" onClick={handleDownloadBOMTemplate} className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Download BOM Template
              </Button>
            </div>

            {/* Upload BOM */}
            <div className="p-4 border rounded-lg bg-blue-50">
              <h4 className="font-medium text-blue-900 mb-2">Step 2: Upload Filled Template</h4>
              <p className="text-sm text-blue-700 mb-3">
                Fill in the template and upload. For Buyer SKU BOM:<br/>
                • Items marked <strong>NOT brand-specific</strong> → Common BOM (Bidso SKU)<br/>
                • Items marked <strong>brand-specific</strong> → Brand-specific BOM
              </p>
              
              {/* Mode Selection */}
              <div className="mb-3 p-3 bg-white rounded border">
                <Label className="text-sm font-medium mb-2 block">Upload Mode:</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="bomUploadMode"
                      value="merge"
                      checked={bomUploadMode === "merge"}
                      onChange={() => setBomUploadMode("merge")}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">
                      <strong>Merge</strong>
                      <span className="text-gray-500 ml-1">(Add new items, update existing)</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="bomUploadMode"
                      value="overwrite"
                      checked={bomUploadMode === "overwrite"}
                      onChange={() => setBomUploadMode("overwrite")}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">
                      <strong>Overwrite</strong>
                      <span className="text-gray-500 ml-1">(Replace entire BOM)</span>
                    </span>
                  </label>
                </div>
                {bomUploadMode === "overwrite" && (
                  <p className="text-xs text-orange-600 mt-2">
                    ⚠️ Overwrite mode will completely replace existing BOM items with uploaded data.
                  </p>
                )}
              </div>
              
              <input
                type="file"
                accept=".xlsx,.xls"
                ref={bomFileInputRef}
                onChange={handleBOMUpload}
                className="hidden"
              />
              <Button 
                onClick={() => bomFileInputRef.current?.click()} 
                disabled={bomUploadLoading}
                className="w-full"
              >
                {bomUploadLoading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Upload BOM Excel
              </Button>
            </div>

            {/* Export Existing BOM */}
            <div className="p-4 border rounded-lg bg-green-50">
              <h4 className="font-medium text-green-900 mb-2">Export Existing BOM</h4>
              <p className="text-sm text-green-700 mb-3">
                Download all current BOM data as Excel.
              </p>
              <Button 
                variant="outline" 
                onClick={handleExportBOM} 
                disabled={bomExportLoading}
                className="w-full"
              >
                {bomExportLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Export All BOM Data
                  </>
                )}
              </Button>
            </div>

            {/* Upload Results */}
            {bomUploadResult && (
              <div className="p-4 border rounded-lg bg-white">
                <h4 className="font-medium mb-2">Upload Results</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-blue-100 rounded">
                    <span className="font-medium">{bomUploadResult.buyer_sku_processed}</span>
                    <span className="text-gray-600 ml-1">Buyer SKUs processed</span>
                  </div>
                  <div className="p-2 bg-purple-100 rounded">
                    <span className="font-medium">{bomUploadResult.bidso_sku_processed}</span>
                    <span className="text-gray-600 ml-1">Bidso SKUs processed</span>
                  </div>
                  <div className="p-2 bg-green-100 rounded">
                    <span className="font-medium">{bomUploadResult.common_bom_updated}</span>
                    <span className="text-gray-600 ml-1">Common BOMs updated</span>
                  </div>
                  <div className="p-2 bg-orange-100 rounded">
                    <span className="font-medium">{bomUploadResult.brand_bom_updated}</span>
                    <span className="text-gray-600 ml-1">Brand BOMs updated</span>
                  </div>
                </div>
                {bomUploadResult.errors?.length > 0 && (
                  <div className="mt-3">
                    <h5 className="text-sm font-medium text-red-600">Errors:</h5>
                    <ul className="text-xs text-red-500 max-h-32 overflow-y-auto">
                      {bomUploadResult.errors.slice(0, 10).map((err, i) => (
                        <li key={i}>• {err}</li>
                      ))}
                      {bomUploadResult.errors.length > 10 && (
                        <li>... and {bomUploadResult.errors.length - 10} more</li>
                      )}
                    </ul>
                  </div>
                )}
                {bomUploadResult.warnings?.length > 0 && (
                  <div className="mt-3">
                    <h5 className="text-sm font-medium text-yellow-600">Warnings:</h5>
                    <ul className="text-xs text-yellow-600 max-h-32 overflow-y-auto">
                      {bomUploadResult.warnings.slice(0, 5).map((w, i) => (
                        <li key={i}>• {w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBOMUploadDialog(false); setBomUploadResult(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={closeBulkDeleteDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Bulk Delete Buyer SKUs
            </DialogTitle>
            <DialogDescription>
              Upload a file containing Buyer SKU IDs to delete. Supports Excel (.xlsx) with 'buyer_sku_id' column or text file with one ID per line.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Upload */}
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                type="file"
                ref={bulkDeleteFileRef}
                onChange={handleBulkDeleteFileSelect}
                accept=".xlsx,.xls,.csv,.txt"
                className="hidden"
              />
              <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
              <p className="text-sm text-gray-600 mb-2">
                {bulkDeleteFile ? bulkDeleteFile.name : 'Upload file with SKU IDs to delete'}
              </p>
              <Button
                variant="outline"
                onClick={() => bulkDeleteFileRef.current?.click()}
                disabled={bulkDeleteLoading}
              >
                {bulkDeleteLoading ? 'Processing...' : 'Select File'}
              </Button>
            </div>

            {/* Preview Results */}
            {bulkDeletePreview && (
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Preview Summary</p>
                    <p className="text-sm text-gray-500">{bulkDeletePreview.message}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-red-600">{bulkDeletePreview.found}</p>
                    <p className="text-xs text-gray-500">SKUs to delete</p>
                  </div>
                </div>

                {bulkDeletePreview.found > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-[250px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-red-50">
                            <TableHead className="text-red-700">#</TableHead>
                            <TableHead className="text-red-700">Buyer SKU ID</TableHead>
                            <TableHead className="text-red-700">Brand</TableHead>
                            <TableHead className="text-red-700">Name</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bulkDeletePreview.skus_to_delete.map((sku, idx) => (
                            <TableRow key={sku.buyer_sku_id}>
                              <TableCell className="text-gray-500">{idx + 1}</TableCell>
                              <TableCell className="font-mono font-medium">{sku.buyer_sku_id}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{sku.brand_code || '-'}</Badge>
                              </TableCell>
                              <TableCell className="truncate max-w-[200px]">{sku.name || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {bulkDeletePreview.not_found > 0 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm font-medium text-yellow-700">
                      {bulkDeletePreview.not_found} SKU IDs not found or already inactive
                    </p>
                    <p className="text-xs text-yellow-600 mt-1">
                      {bulkDeletePreview.not_found_ids?.slice(0, 5).join(', ')}
                      {bulkDeletePreview.not_found > 5 && '...'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeBulkDeleteDialog}>
              Cancel
            </Button>
            {bulkDeletePreview?.found > 0 && (
              <Button
                variant="destructive"
                onClick={handleConfirmBulkDelete}
                disabled={bulkDeleteLoading}
              >
                {bulkDeleteLoading ? 'Deleting...' : `Delete ${bulkDeletePreview.found} SKUs`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Buyer SKU HSN/GST Dialog */}
      <Dialog open={showEditBuyerDialog} onOpenChange={setShowEditBuyerDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit HSN/GST</DialogTitle>
            <DialogDescription>
              Update tax details for {selectedBuyerSKU?.buyer_sku_id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">{selectedBuyerSKU?.name}</p>
              <p className="text-xs text-gray-400">Brand: {selectedBuyerSKU?.brand_code}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>HSN Code</Label>
                <Input
                  placeholder="e.g., 87141090"
                  value={editBuyerForm.hsn_code}
                  onChange={(e) => setEditBuyerForm({ ...editBuyerForm, hsn_code: e.target.value })}
                  data-testid="hsn-code-input"
                />
                <p className="text-xs text-gray-400 mt-1">8-digit HSN code</p>
              </div>
              <div>
                <Label>GST Rate (%)</Label>
                <Select 
                  value={String(editBuyerForm.gst_rate)} 
                  onValueChange={(v) => setEditBuyerForm({ ...editBuyerForm, gst_rate: parseInt(v) })}
                >
                  <SelectTrigger data-testid="gst-rate-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% (Exempt)</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="12">12%</SelectItem>
                    <SelectItem value="18">18%</SelectItem>
                    <SelectItem value="28">28%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditBuyerDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveBuyerHSNGST} disabled={editBuyerLoading}>
              {editBuyerLoading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk HSN/GST Upload Dialog */}
      <Dialog open={showBulkHSNDialog} onOpenChange={setShowBulkHSNDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Upload HSN/GST</DialogTitle>
            <DialogDescription>
              Upload an Excel file to update HSN codes and GST rates for multiple SKUs
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-700 mb-2">Template Format</h4>
              <p className="text-sm text-blue-600 mb-3">
                Excel must have columns: <code className="bg-blue-100 px-1 rounded">buyer_sku_id</code>, <code className="bg-blue-100 px-1 rounded">hsn_code</code>, <code className="bg-blue-100 px-1 rounded">gst_rate</code>
              </p>
              <Button variant="outline" size="sm" onClick={handleDownloadHSNTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>
            
            <div>
              <Label>Select Excel File</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setBulkHSNFile(e.target.files?.[0] || null)}
                className="mt-1"
                data-testid="hsn-upload-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBulkHSNDialog(false); setBulkHSNFile(null); }}>
              Cancel
            </Button>
            <Button onClick={handleBulkHSNUpload} disabled={bulkHSNLoading || !bulkHSNFile}>
              {bulkHSNLoading ? "Uploading..." : "Upload & Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tech Pack Upload Dialog (Feb 2026) — per-row image management.
          Six fixed slots, file picker per slot, deterministic 6-row grid
          so layout stays identical whether 0 or 6 are filled. */}
      <Dialog open={showTechpackDialog} onOpenChange={setShowTechpackDialog}>
        <DialogContent className="max-w-2xl" data-testid="techpack-dialog">
          <DialogHeader>
            <DialogTitle>
              Tech Pack Images
              {techpackSku ? (
                <span className="text-sm font-mono text-muted-foreground ml-2">
                  · {techpackSku.buyer_sku_id}
                </span>
              ) : null}
            </DialogTitle>
            <DialogDescription>
              Upload up to six images. Each slot is a fixed position on the Tech Pack PDF —
              empty slots render as &ldquo;No image uploaded&rdquo; placeholders.
            </DialogDescription>
          </DialogHeader>
          {techpackLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
              Loading...
            </div>
          ) : (
            <div className="space-y-3 py-2">
              {TECHPACK_SLOTS.map((meta) => {
                const slotState = (techpackSlots || []).find((s) => s.slot === meta.key) || {};
                const uploaded = !!slotState.uploaded;
                const busy = techpackBusySlot === meta.key;
                return (
                  <div
                    key={meta.key}
                    className="flex items-center justify-between gap-3 border rounded-md p-3"
                    data-testid={`techpack-slot-row-${meta.key}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{meta.label}</div>
                      <div className="text-xs text-muted-foreground font-mono truncate">
                        {meta.key}
                        {uploaded && slotState.width && slotState.height
                          ? ` · ${slotState.width}×${slotState.height}px`
                          : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {uploaded ? (
                        <Badge variant="default" className="gap-1">
                          <Check className="h-3 w-3" /> Uploaded
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Empty</Badge>
                      )}
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/png,image/jpeg"
                          className="hidden"
                          disabled={busy}
                          data-testid={`techpack-file-input-${meta.key}`}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f && techpackSku) {
                              uploadTechpackImage(techpackSku.buyer_sku_id, meta.key, f);
                            }
                            // Reset so re-selecting the same file fires onChange again.
                            e.target.value = "";
                          }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy}
                          asChild
                          data-testid={`techpack-pick-${meta.key}`}
                        >
                          <span>
                            {busy ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Upload className="h-3.5 w-3.5" />
                            )}
                            <span className="ml-2">{uploaded ? "Replace" : "Upload"}</span>
                          </span>
                        </Button>
                      </label>
                      {uploaded && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          onClick={() =>
                            techpackSku && deleteTechpackImage(techpackSku.buyer_sku_id, meta.key)
                          }
                          data-testid={`techpack-clear-${meta.key}`}
                          title="Clear this slot"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => techpackSku && downloadTechpack(techpackSku)}
              data-testid="techpack-dialog-download-btn"
            >
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
            <Button onClick={() => setShowTechpackDialog(false)} data-testid="techpack-dialog-close">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SKUManagement;
